/**
 * The French message catalogue, the app's default language (FR-035, constitution 1.2.0).
 * Screens never import it directly: they use `t()` from `app/src/i18n/index.ts`, which picks
 * this catalogue or the English one (`en.ts`, T076c) for the signed-in person. Every key here
 * needs an English text too. See specs/001-wine-cellar-tracker/contracts/ui-and-emails.md for the
 * intended French wording this mirrors.
 */

export const fr = {
  // Common actions, used across many screens
  'common.cancel': 'Annuler',
  'common.back': 'Retour',
  'home.reduce': 'Boire une bouteille',
  'drink.search': 'Rechercher un vin',
  'drink.searchPlaceholder': 'Domaine ou parcelle',
  'drink.noMatch': 'Aucun vin en stock ne correspond.',
  'rating.up': 'Augmenter la note',
  'rating.down': 'Baisser la note',
  'rating.noRating': 'Pas de note',
  'common.confirm': 'Confirmer',
  'common.delete': 'Supprimer',
  'common.save': 'Enregistrer',
  'common.signOut': 'Se déconnecter',
  'common.loading': 'Chargement…',

  // Sign-in (/connexion)
  'signIn.welcome': 'Bienvenue dans votre cave à vin personnelle\u00a0!',
  'signIn.emailLabel': 'Email',
  'signIn.emailPlaceholder': 'abc@mail.com',
  'signIn.submit': 'Se connecter',
  // Real sign-in (T074a). One message for a wrong password, an unknown address or a stopped
  // person: the page never reveals which (FR-024).
  'signIn.passwordLabel': 'Mot de passe',
  'signIn.showPassword': 'Afficher le mot de passe',
  'signIn.hidePassword': 'Masquer le mot de passe',
  'signIn.forgot': 'Mot de passe oublié\u00a0?',
  'signIn.refused': 'Email ou mot de passe incorrect, ou accès non autorisé.',
  'signIn.tooManyAttempts': 'Trop de tentatives. Patientez quelques minutes, puis réessayez.',
  'signIn.network':
    'Impossible de joindre le service de connexion. Vérifiez votre connexion et réessayez.',
  'newPassword.title': 'Choisissez votre mot de passe',
  'newPassword.intro':
    'Pour votre première connexion, remplacez le mot de passe temporaire reçu par email.',
  'newPassword.label': 'Nouveau mot de passe',
  'newPassword.confirm': 'Confirmez le mot de passe',
  'newPassword.mismatch': 'Les deux mots de passe ne sont pas identiques.',
  'newPassword.invalid': 'Ce mot de passe n’est pas accepté. Vérifiez les règles ci-dessus.',
  'newPassword.submit': 'Enregistrer et se connecter',
  'reset.title': 'Mot de passe oublié',
  'reset.intro':
    'Indiquez votre email : vous recevrez un code pour choisir un nouveau mot de passe.',
  'reset.send': 'Envoyer le code',
  'reset.codeSent': 'Si cette adresse est autorisée, un code vient de lui être envoyé par email.',
  'reset.codeLabel': 'Code reçu par email',
  'reset.submit': 'Changer le mot de passe',
  'reset.invalidCode': 'Ce code est incorrect ou a expiré.',
  'reset.done': 'Mot de passe changé. Vous pouvez vous connecter.',
  'reset.back': 'Retour à la connexion',

  // Top bar, and the owner's cellar switcher in the settings (FR-040, SC-014, owner feedback
  // 2026-09-28), contracts/ui-and-emails.md
  'settings.cellarName': 'Nom de la cave',
  'error.cellarNameTooLong': 'Le nom de la cave doit faire au plus 60 caractères.',
  'cellar.mine': 'Ma cave',
  'cellar.of': 'Cave de {name}',
  'cellar.switchTitle': 'Changer de cave',
  'cellar.choose': 'Choisir une cave',
  'cellar.usersTitle': 'Utilisateurs de l’application',
  'cellar.usersHint':
    'Qui peut se connecter à l’application. Chaque utilisateur a sa propre cave : cette liste ne dépend pas de la cave affichée.',
  'cellar.manageUsers': 'Gérer les utilisateurs et leurs caves',
  'cellar.settings': 'Réglages',
  'nav.label': 'Navigation principale',
  'nav.home': 'Accueil',
  'nav.stock': 'Stock',
  'nav.expiring': 'À boire',
  'expiring.none': 'Aucun vin dépassé ni à boire d’ici 24 mois.',
  'expiring.within': 'À boire dans les 24 mois',
  'expiring.past': 'Date dépassée',
  'home.drunk': 'Bouteille enregistrée : {wine}.',
  'home.drunkMany': '{bottles} enregistrées : {wine}.',
  'home.past': 'À boire',
  'filters.due': 'Échéance',
  'cellar.loadFailed':
    'Impossible de charger cette cave. Vérifiez votre connexion et rechargez la page.',

  // Drinking a bottle (FR-012 as changed by the owner, 2026-09-28): nothing is recorded until
  // "Valider"
  'drink.action': 'Boire une bouteille',
  'drink.title': 'Bouteille bue',
  'drink.rating': 'Note',
  'drink.notes': 'Commentaire',
  'drink.confirm': 'Valider',
  'rating.outOfFive': '{rating}/5',
  'rating.none': 'Pas de note',
  'rating.trendUp': 'Note en hausse',
  'rating.trendDown': 'Note en baisse',
  'rating.trendSame': 'Note stable',
  'tasting.outsideCellar': 'hors cave',
  'drink.bottles': 'Bouteilles bues',
  'drink.bottlesLess': 'Une bouteille de moins',
  'drink.bottlesMore': 'Une bouteille de plus',

  // Wine, purchase and tasting fields (FR-001, FR-007, FR-013, FR-035). Moved from the screens
  // into the catalogue for English (T076a).
  'wine.legend': 'Détails du vin',
  'wine.domaine': 'Domaine',
  'wine.cepage': 'Cépage',
  'wine.chooseCepage': 'Choisir…',
  'wine.parcelle': 'Parcelle',
  'wine.annee': 'Année',
  'wine.pays': 'Pays',
  'wine.choosePays': 'Choisir un pays',
  'wine.region': 'Région',
  'wine.regionOther': 'Autre région',
  'wine.regionMissing': 'À compléter',
  'wine.chooseRegion': 'Choisir une région',
  'wine.regionNeedsCountry': 'Choisir d’abord un pays',
  'error.invalidRegion': 'Choisissez une région de ce pays.',
  'wine.typeDeVin': 'Type',
  'wine.chooseType': 'Choisir un type',
  'wine.chooseAnnee': 'Choisir une année',
  'wineType.rouge': 'Rouge',
  'wineType.blanc': 'Blanc',
  'wineType.rose': 'Rosé',
  'wineType.vendanges_tardives': 'Vendanges tardives',
  'stock.one': '{count} bouteille',
  'stock.other': '{count} bouteilles',

  'drinkUntil.legend': 'À boire avant',
  'drinkUntil.none': 'Aucune',

  'purchase.title': 'Ajouter un achat',
  'purchase.legend': 'Détails de l’achat',
  'purchase.quantity': 'Quantité',
  'purchase.unitPrice': 'Prix (€)',
  'purchase.date': 'Date d’achat',
  'purchase.notes': 'Détails',
  'purchase.submit': 'Enregistrer',
  'purchase.addToExisting': 'Ajouter l’achat à ce vin',
  'purchase.missingFields': 'Merci de compléter tous les champs obligatoires.',

  'tasting.history': 'Historique',
  'tasting.noRating': 'Pas de note',
  'tasting.notes': 'Notes de dégustation',
  'tasting.date': 'Date de dégustation',

  // Rule errors from app/src/domain shown to people (app/src/i18n/errors.ts)
  'error.invalidQuantity': 'La quantité doit être un nombre entier d’au moins 1.',
  'error.invalidPrice': 'Le prix unitaire doit être un nombre entier d’euros, positif ou nul.',
  'error.invalidDate': 'Cette date n’existe pas.',
  'error.invalidRating': 'La note doit être comprise entre 1 et 5, par demi-point.',
  'error.wineNotFound': 'Ce vin n’existe plus dans votre cave.',
  'error.wineDuplicate': 'Ce vin existe déjà dans votre cave.',

  // Month names, for every month picker (owner feedback, 2026-09-28)
  'edit.field': 'Modifier : {field}',
  'edit.open': 'Modifier',
  'edit.close': 'Fermer',
  'edit.none': '—',
  'purchase.history': 'Achats',
  'purchase.of': 'Achat du {date}',
  'photos.title': 'Étiquette',
  'photos.front': 'Avant',
  'photos.back': 'Dos',
  'photos.takeFront': 'Photographier l’avant',
  'photos.takeBack': 'Photographier le dos',
  'photos.removeFront': 'Supprimer la photo de l’avant',
  'photos.removeBack': 'Supprimer la photo du dos',
  'photos.removeConfirm': 'Supprimer cette photo ?',
  'photos.alt': 'Étiquette de {wine} ({side})',
  'photos.none': 'Pas encore de photo',
  'photos.loading': 'Chargement…',
  'photos.saving': 'Enregistrement…',
  'photos.failed': 'La photo n’a pas pu être enregistrée. Réessayez.',
  'purchase.tastedElsewhere':
    'Aucune bouteille achetée : un vin dégusté ailleurs. Sa note et son commentaire vont dans son historique.',
  'purchase.delete': 'Supprimer cet achat',
  'purchase.deleteWarning': 'Cet achat sera supprimé et le stock recalculé.',
  'tasting.delete': 'Supprimer cette dégustation',
  'tasting.deleteWarning':
    'Cette dégustation sera supprimée. Si la bouteille venait de la cave, elle y revient.',
  'wine.delete': 'Supprimer ce vin',
  'wine.deleteWarning':
    'Ce vin, tous ses achats et toutes ses dégustations seront supprimés. C’est définitif dans l’application.',
  'delete.confirm': 'Supprimer définitivement',
  'error.wineDuplicateNamed': 'Ce vin existe déjà dans votre cave : {wine}.',
  'error.invalidDomaine': 'Le Domaine est obligatoire.',
  'error.invalidCepage': 'Le Cépage est obligatoire.',
  'error.invalidAnnee': 'L’année doit être un millésime entre 1800 et l’an prochain.',
  'error.invalidPays': 'Choisissez un pays de la liste.',
  'error.invalidTypeDeVin': 'Choisissez un type de vin.',
  'error.invalidDrinkUntil': 'L’échéance de dégustation n’est pas valable.',
  'error.invalidMonth': 'Le mois n’est pas valable.',

  'map.link': 'Carte',
  'map.title': 'Par pays',
  'map.label': 'Carte du monde : les pays de vos vins sont en couleur',
  'map.hint': 'Touchez un pays, sur la carte ou dans la liste, pour voir ses vins.',
  'map.empty': 'Aucun vin pour l’instant : la carte se remplira avec vos achats.',
  'map.purchased': 'Achetées',
  'map.consumed': 'Bues',
  'map.average': 'Note moyenne',
  'map.noRating': 'pas d’appréciation',
  'map.countrySummary': '{country} : {purchased}, {consumed}, note {rating}',
  'map.bought.one': '{count} achetée',
  'map.bought.other': '{count} achetées',
  'map.drunk.one': '{count} bue',
  'map.drunk.other': '{count} bues',
  'map.wines': 'Vins',
  'map.filters': 'Filtres de la carte',
  'map.status': 'Statut',
  'map.status.drunk': 'Bues',
  'map.status.notDrunk': 'En stock',

  'digest.subject.one': 'Votre cave : 1 vin à surveiller',
  'digest.subject.other': 'Votre cave : {count} vins à surveiller',
  'digest.greeting': 'Bonjour,',
  'digest.intro': 'Voici les vins de votre cave qui arrivent à une étape de leur échéance :',
  'digest.reminder.36': 'Plus que 3 ans',
  'digest.reminder.24': 'Plus que 2 ans',
  'digest.reminder.12': 'Plus que 1 an',
  'digest.reminder.6': 'Plus que 6 mois',
  'digest.reminder.0': 'Échéance atteinte',
  'digest.open': 'Ouvrir votre cave : {url}',
  'digest.optOut': 'Pour ne plus recevoir cet e-mail : {url}',
  'settings.digest': 'E-mail mensuel',
  'settings.digestLabel': 'Recevoir chaque mois la liste des vins à boire bientôt',
  'settings.digestFailed': 'Ce choix n’a pas pu être enregistré. Réessayez.',

  'camera.stepFront': 'Photo 1 sur 2\u00a0: l’avant de la bouteille',
  'camera.stepBack': 'Photo 2 sur 2\u00a0: le dos de la bouteille',
  'camera.takeFront': 'Photographier l’avant',
  'camera.takeBack': 'Photographier le dos',
  'camera.skipBack': 'Sans le dos',
  'camera.unavailable':
    'Caméra non disponible dans l’app. Touchez de nouveau «\u00a0Scanner la bouteille\u00a0» pour utiliser l’appareil photo du téléphone.',
  'scan.action': 'Scanner la bouteille',
  'scan.reading': 'Lecture de l’étiquette…',
  'scan.frontTaken': 'Avant de la bouteille photographié. Ajouter le dos\u00a0?',
  'scan.frontPhoto': 'Photo de l’avant de la bouteille',
  'scan.addBack': 'Photographier le dos',
  'scan.frontOnly': 'Lire cette photo seule',
  'scan.done': 'Étiquette lue.',
  'scan.failed':
    'Impossible de lire l’étiquette. Réessayez avec une photo plus nette ou saisissez le vin à la main.',
  'scan.unavailable':
    'La lecture des étiquettes n’est pas disponible pour le moment : saisissez le vin à la main.',
  'scan.matchesExisting': 'Ce vin est déjà dans votre cave ({wine}) : l’achat lui sera ajouté.',

  'rating.online': 'Note en ligne',
  'rating.onlineSearching': 'Recherche des notes…',
  'rating.onlineA11y': 'Note en ligne : {rating}/5',
  'rating.cardA11y': 'Note {rating}/5',
  'rating.onlineNotes': 'Notes',
  'rating.onlineNote': '{day} : {site} {score} / {scale}',
  'assist.quotaDay':
    'Quota gratuit de Gemini épuisé pour aujourd’hui : réessayez demain (il repart vers 9 h, heure de Paris).',
  'assist.quotaMinute':
    'Trop de demandes à Gemini en une minute (quota gratuit) : réessayez dans une minute.',
  'assist.quota': 'Quota gratuit de Gemini épuisé pour le moment : réessayez plus tard.',
  'rating.onlineNone': 'Aucune note trouvée en ligne pour ce vin.',
  'rating.onlineUnavailable': 'La recherche de notes n’est pas disponible pour le moment.',
  'complete.action': 'Recherche en ligne',
  'complete.searching': 'Recherche en ligne…',
  'complete.done': 'Complété en ligne.',
  'complete.none': 'Rien trouvé en ligne pour ce vin.',
  'complete.unavailable': 'La recherche en ligne n’est pas disponible pour le moment.',
  'suggest.searchSuggestions': 'Recherches Google associées',

  'month.1': 'janvier',
  'month.2': 'février',
  'month.3': 'mars',
  'month.4': 'avril',
  'month.5': 'mai',
  'month.6': 'juin',
  'month.7': 'juillet',
  'month.8': 'août',
  'month.9': 'septembre',
  'month.10': 'octobre',
  'month.11': 'novembre',
  'month.12': 'décembre',

  // Drink-by colours, badge and count (User Story 6, FR-033, FR-037, FR-041,
  // contracts/ui-and-emails.md "Inventory card")
  'drinkBy.before': 'À boire avant {date}',

  // Filters and search on the inventory (User Story 4, FR-019 to FR-022)
  'filters.search': 'Rechercher',
  'filters.searchPlaceholder': 'Domaine, cépage, note de dégustation…',
  'filters.show': 'Filtres',
  'filters.showActive': 'Filtres ({count})',
  'filters.hide': 'Masquer les filtres',
  'filters.any': 'Tous',
  'filters.searchList': 'Rechercher dans la liste',
  'filters.add': 'Ajouter « {value} »',
  'filters.rating': 'Note',
  'filters.includeEmpty': 'Inclure les vins épuisés',
  'filters.reset': 'Réinitialiser les filtres',
  'filters.count.one': '{count} vin',
  'filters.count.other': '{count} vins',
  'filters.empty': 'Aucun vin ne correspond à ces filtres.',
  'filters.allFinished': 'Toutes les bouteilles de cette cave ont été bues.',
  'filters.showFinished': 'Afficher les vins épuisés',

  // Inventory (/c/{cellarId})
  'inventory.metrics': 'Chiffres clés',
  'inventory.metricBottles': 'Stock',
  'inventory.bottleUnit.one': 'bouteille',
  'inventory.bottleUnit.other': 'bouteilles',
  'inventory.metricExpired': 'À boire',
  'inventory.metricNone': '—',
  'inventory.addPurchase': 'Ajouter une bouteille',
  'inventory.showSearch': 'Rechercher',
  'inventory.empty': 'Votre cave est vide. Ajoutez votre premier achat pour commencer.',
  'inventory.emptyOther': 'Cette cave est vide.',

  // Deleting a cellar's contents (FR-039, FR-036)
  'deleteCellar.action': 'Supprimer cette cave',
  'deleteCellar.warning':
    'Tous les vins, achats et dégustations de la cave seront effacés. Cette action est irréversible. Êtes-vous vraiment sûr ?',
  'deleteCellar.acknowledge': 'Je comprends que cette suppression est définitive.',
  'deleteCellar.confirm': 'Supprimer',
  'deleteCellar.done': 'La cave a été vidée.',
  'deleteCellar.missing':
    'Cet utilisateur ne s’est pas encore connecté : sa cave n’existe pas encore.',
  'deleteCellar.failed': 'Impossible de supprimer la cave. Rien n’a été effacé, réessayez.',

  // Own settings (/reglages)
  'export.own': 'Exporter ma cave en CSV',
  'export.other': 'Exporter cette cave en CSV',
  'export.empty': 'La cave est vide : le fichier ne contient que les titres des colonnes.',
  'export.failed': 'L’export n’a pas pu être fait. Vérifiez votre connexion et réessayez.',

  'settings.title': 'Réglages',
  'settings.language': 'Langue',
  'settings.languageFailed': 'Impossible d’enregistrer votre langue. Réessayez.',
  // Each language is always written in itself.
  'language.fr': 'Français',
  'language.en': 'English',

  // Choosing a password (first sign-in, forgotten password), FR-045
  'password.rules':
    'Au moins 16 caractères, dont 4 chiffres, 4 lettres et 4 caractères spéciaux (par exemple ! @ # - _).',
  'password.tooShort': 'Au moins 16 caractères.',
  'password.notEnoughDigits': 'Au moins 4 chiffres.',
  'password.notEnoughLetters': 'Au moins 4 lettres.',
  'password.notEnoughSpecial': 'Au moins 4 caractères spéciaux (par exemple ! @ # - _).',

  // Users screen (/admin/personnes), contracts/ui-and-emails.md and contracts/admin-function.md.
  // The owner asked for "utilisateur", never "personne" (2026-09-28).
  'admin.limitReached':
    'Vous avez atteint le nombre maximal d’utilisateurs autorisés (4, en plus de vous).',
  'admin.alreadyExists': 'Cette adresse est déjà autorisée.',
  'admin.cannotDisableOwner': "Vous ne pouvez pas couper l'accès du propriétaire.",
  'admin.invalidEmail': "Cette adresse e-mail n'est pas valide.",
  'admin.notFound': "Cet utilisateur n'existe plus. Rechargez la liste.",
  'admin.forbidden': 'Seul le propriétaire peut gérer les utilisateurs.',
  'admin.unavailable': 'Le service est momentanément indisponible. Réessayez plus tard.',
  'admin.network': 'Impossible de joindre le service. Vérifiez votre connexion et réessayez.',
  'admin.title': 'Utilisateurs de l’application',
  'admin.intro':
    'Vous pouvez ajouter jusqu’à 4 utilisateurs, en plus de vous. Chaque cave a un seul utilisateur.',
  'admin.addEmailLabel': 'Adresse e-mail de l’utilisateur',
  'admin.clearEmail': 'Effacer l’adresse e-mail',
  'admin.add': 'Ajouter un utilisateur',
  'admin.added':
    'Invitation envoyée à {email}. Amazon Web Services (AWS) lui enverra aussi un e-mail de confirmation avec un lien pour activer son e-mail mensuel. Cet e-mail peut arriver dans les spams.',
  'admin.invited': 'Invité',
  'admin.owner': 'Propriétaire (vous)',
  'admin.enabled': 'Accès accordé',
  'admin.disabled': 'Accès retiré',
  'admin.accessSwitch': 'Accès de {email} à l’application',

  // Reached when a cellar address is denied or does not exist (FR-027, the
  // app never reveals which of the two it is)
  'notFound.title': 'Page introuvable',
  'notFound.body': "Cette page n'existe pas ou vous n'y avez pas accès.",

  // Errors and confirmations shared across features (contracts/ui-and-emails.md)
  'error.wineAlreadyExists': 'Ce vin existe déjà dans votre cave. Ajouter l’achat à ce vin ?',
  'error.stockEmpty':
    "Il ne reste plus de bouteille en stock. Enregistrez d'abord un achat, ou ajoutez une dégustation hors stock.",
  'error.stockWouldBeNegative': 'Cette modification ferait passer le stock sous zéro.',
  'error.recordChangedElsewhere':
    'Cette fiche a été modifiée entre-temps. Rechargez-la avant de continuer.',
  'error.futureMonth': 'Le mois choisi est dans le futur.',
  'error.saveFailed': "Impossible d'enregistrer. La modification a été annulée, réessayez.",
  'error.undoFailed': "Impossible d'annuler, réessayez.",
  'error.rateFailed': "Impossible d'enregistrer la note, réessayez.",
} as const;

export type MessageKey = keyof typeof fr;
